import {
  Sprite,
  Point,
  Text,
} from 'pixi.js';
import WebGLElement from './WebGLElement';
import overlay from '.';
import { getRatio, isEdge } from '../utils';

class WebGLVideo extends WebGLElement {
  constructor(component, element, src, resource) {
    super(component, element);

    this.element = element;
    this.src = src;
    this.resource = resource;
    this.vide = null;

    this.mask = null;
    this.loadedHandler = () => {
      this.video = overlay.ref.app.loader.resources[this.resource].data;
      this.video.autoplay = true;
      this.video.muted = true;
      this.video.loop = true;
      this.video.setAttribute('playsinline', true);
      this.video.pause();
      this.sprite = Sprite.from(this.video);

      if (this.component.mode === 'contain') {
        this.mask = new Sprite(overlay.ref.app.loader.resources.maskMid.texture);
      }
      if (this.component.mode === 'cover') {
        this.mask = new Sprite(overlay.ref.app.loader.resources.maskWide.texture);
      }
      if (this.component.mode === 'padding') {
        this.mask = new Sprite(overlay.ref.app.loader.resources.maskSmall.texture);
      }
      if (this.component.mode === 'half') {
        this.mask = new Sprite(overlay.ref.app.loader.resources.maskSmall.texture);
      }

      this.setProps();

      this.sprite.filters = [overlay.ref.displace];
      if (this.price) {
        this.price.filters = [overlay.ref.displace];
        this.priceExclusions.filters = [overlay.ref.displace];
        this.buyIt.filters = [overlay.ref.displace];
      }

      if (this.subtitle) {
        this.subtitle.filters = [overlay.ref.displace];
      }

      overlay.ref.push(this);
    };
    window.addEventListener('webgl-loaded', this.loadedHandler);

    if (this.component.data.content.price) {
      this.price = new Text(this.component.data.content.price, {
        fontFamily: 'VHS',
        fontSize: 40,
        fill: 0xffffff,
      });
      this.priceExclusions = new Text('Exclusions Apply*', {
        fontFamily: 'VHS',
        fontSize: 16,
        fill: 0xffffff,
      });
      this.buyIt = new Text('BUY IT', {
        fontFamily: 'VHS',
        fontSize: 40,
        fill: 0xffffff,
      });
    }

    if (this.component.data.content.subtitle) {
      this.subtitle = new Text(this.component.data.content.subtitle, {
        fontFamily: 'VHS',
        fontSize: 40,
        fill: 0xffffff,
      });
    }
  }

  init() {
    this.element.style.visibility = 'hidden';

    this.render(true);
    this.renderHandler = () => { this.render(); };
    window.addEventListener('render', this.renderHandler);
  }

  setProps() {
    this.sprite.originalWidth = this.sprite.width;
    this.sprite.originalHeight = this.sprite.height;
    this.setSprite();
    this.sprite.anchor = new Point(0.5, 0.5);

    if (this.price) {
      this.price.zIndex = 3;
      this.priceExclusions.zIndex = 3;
      this.buyIt.zIndex = 3;
      this.buyIt.alpha = 0;
      overlay.ref.app.stage.addChild(this.price);
      overlay.ref.app.stage.addChild(this.priceExclusions);
      overlay.ref.app.stage.addChild(this.buyIt);
    }

    if (this.subtitle) {
      this.subtitle.zIndex = 3;
      overlay.ref.app.stage.addChild(this.subtitle);
    }

    this.setMask();
    this.mask.anchor = new Point(0.5, 0.5);
    overlay.ref.app.stage.addChild(this.mask);
    this.sprite.mask = this.mask;
  }

  render(first = false) {
    if (this.component.$el.dataset.inViewport !== '' && !first) {
      this.sprite.visible = false;
      this.sprite.renderable = false;
      if (isEdge()) this.sprite.alpha = 0;
      return;
    }
    this.sprite.visible = true;
    this.sprite.renderable = true;
    if (this.video.paused) this.video.play();
    if (isEdge()) this.sprite.alpha = 1;
    const rect = this.element.getBoundingClientRect();
    this.sprite.position.x = rect.left + (rect.width / 2);
    this.sprite.position.y = rect.top + (rect.height / 2);
    this.mask.position.x = this.sprite.position.x;
    this.mask.position.y = this.sprite.position.y;

    if (this.price) {
      this.price.scale.x = getRatio().x;
      this.price.scale.y = getRatio().x;
      this.price.position.x = (rect.left + (rect.width / 2)) - this.price.width / 2;
      this.price.position.y = (rect.top + rect.height) - 100;
      this.priceExclusions.scale.x = getRatio().x;
      this.priceExclusions.scale.y = getRatio().x;
      this.priceExclusions.x = (rect.left + (rect.width / 2)) - this.priceExclusions.width / 2;
      this.priceExclusions.position.y = this.price.position.y + this.priceExclusions.height + 20;
      this.buyIt.scale.x = getRatio().x;
      this.buyIt.scale.y = getRatio().x;
      this.buyIt.position.x = (rect.left + (rect.width / 2)) - this.buyIt.width / 2;
      this.buyIt.position.y = rect.top + 60;
    }

    if (this.subtitle) {
      this.subtitle.scale.x = getRatio(false).x;
      this.subtitle.scale.y = getRatio(false).x;
      this.subtitle.position.x = (rect.left + (rect.width / 2)) - this.subtitle.width / 2;
      this.subtitle.position.y = (rect.top + rect.height) - 100;
    }
  }

  resize() {
    this.setSprite();
    this.setMask();
    this.render(true);
  }

  initial() {}

  in() {}

  setSprite() {
    const rect = this.element.getBoundingClientRect();
    const rw = rect.width / this.sprite.originalWidth;
    const rh = rect.height / this.sprite.originalHeight;
    let r = 1;
    if (this.component.mode === 'contain') {
      r = Math.min(rw, rh);
    }
    if (this.component.mode === 'cover' || this.component.mode === 'half' || this.component.mode === 'padding') {
      r = Math.max(rw, rh);
    }
    this.sprite.scale = new Point(r, r);
  }

  setMask() {
    const rect = this.element.getBoundingClientRect();
    this.mask.width = rect.width;
    this.mask.height = rect.height;

    if (this.component.mode === 'contain') {
      this.mask.width = this.sprite.width;
      this.mask.height = this.sprite.height;
    }
  }

  destroy() {
    window.removeEventListener('webgl-loaded', this.loadedHandler);
    window.removeEventListener('render', this.renderHandler);
    this.mask.destroy();
    if (this.price) this.price.destroy({ children: true });
    if (this.priceExclusions) this.priceExclusions.destroy({ children: true });
    if (this.buyIt) this.buyIt.destroy({ children: true });
    if (this.subtitle) this.subtitle.destroy({ children: true });
    this.sprite.destroy({ children: true });
  }
}

export default WebGLVideo;
